import { type PayAppEntry, type ProgressBilling, type ScheduleLine, computePayApp, formatPercentMilli, parsePercentMilli, percentOf } from "@nmt/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { errorMessage, get, post } from "../api";
import { Alert, Badge, Button, Dialog, Field, Panel, PanelHeader, formatCents, formatDate, parseDollars } from "../components/ui";
import { useSession } from "../session";

/** Progress billing (pay applications against the schedule of values) for the job's signed custom proposals billed that way. */
export function ProgressBillingPanels({ jobId, onOpenInvoice }: { jobId: string; onOpenInvoice: (id: string) => void }) {
  const q = useQuery({ queryKey: ["progress", jobId], queryFn: () => get<ProgressBilling[]>(`/api/jobs/${jobId}/progress`) });
  if (!q.data?.length) return null;
  return (
    <>
      {q.data.map((pg) => (
        <ProgressPanel key={pg.proposalId} jobId={jobId} pg={pg} onOpenInvoice={onOpenInvoice} />
      ))}
    </>
  );
}

function ProgressPanel({ jobId, pg, onOpenInvoice }: { jobId: string; pg: ProgressBilling; onOpenInvoice: (id: string) => void }) {
  const { can } = useSession();
  const qc = useQueryClient();
  const [billing, setBilling] = useState(false);
  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: ["progress", jobId] });
    await qc.invalidateQueries({ queryKey: ["invoices", jobId] });
  };
  const release = useMutation({ mutationFn: () => post(`/api/proposals/${pg.proposalId}/release-retainage`), onSuccess: refresh });
  const billed = pg.payApps.filter((x) => x.status === "billed");
  const last = billed.at(-1);
  const contract = pg.schedule.reduce((t, l) => t + l.valueCents, 0);
  const edit = can("invoices.edit");
  return (
    <Panel>
      <PanelHeader
        title={`Progress billing: ${pg.title}`}
        description={[
          `Contract ${formatCents(contract)}${contract !== pg.originalCents ? ` (with ${formatCents(contract - pg.originalCents)} in change orders)` : ""}`,
          last ? `${formatCents(last.summary.earnedLessRetainageCents)} billed` : "Nothing billed yet",
          pg.retainageHeldCents ? `${formatCents(pg.retainageHeldCents)} retainage held` : null,
          last ? `${formatCents(last.summary.balanceToFinishCents)} left to bill` : null,
        ]
          .filter(Boolean)
          .join(" · ")}
        action={
          edit && (
            <div className="flex flex-wrap gap-2">
              {pg.retainageHeldCents > 0 && !pg.release && (
                <Button variant="secondary" className="px-3 py-1.5" busy={release.isPending} onClick={() => window.confirm(`Bill the ${formatCents(pg.retainageHeldCents)} of retainage held? Do this at final completion; no more pay apps after it.`) && release.mutate()}>
                  Release retainage
                </Button>
              )}
              {!pg.release && (
                <Button variant="primary" className="px-3 py-1.5" onClick={() => setBilling(true)}>
                  New pay app #{pg.nextNumber}
                </Button>
              )}
            </div>
          )
        }
      />
      {release.error && (
        <div className="px-5 pt-3">
          <Alert>{errorMessage(release.error)}</Alert>
        </div>
      )}
      {pg.payApps.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead className="text-left text-xs text-muted">
              <tr className="border-b border-mist">
                <th className="px-5 py-2 font-semibold">Pay app</th>
                <th className="py-2 font-semibold">Period to</th>
                <th className="py-2 text-right font-semibold">Completed to date</th>
                <th className="py-2 text-right font-semibold">Retainage held</th>
                <th className="py-2 text-right font-semibold">Due on this one</th>
                <th className="px-5 py-2 font-semibold">Invoice</th>
              </tr>
            </thead>
            <tbody>
              {pg.payApps.map((x) => (
                <tr key={x.id} className={x.status === "void" ? "border-b border-mist text-muted line-through" : "border-b border-mist"}>
                  <td className="px-5 py-2 font-semibold">#{x.number}</td>
                  <td className="py-2">{formatDate(x.periodTo)}</td>
                  <td className="py-2 text-right">
                    {formatCents(x.summary.completedToDateCents)} <span className="text-xs text-muted">({formatPercentMilli(Math.round((x.summary.completedToDateCents / Math.max(1, x.summary.contractToDateCents)) * 100) * 1000)})</span>
                  </td>
                  <td className="py-2 text-right">{formatCents(x.summary.retainageCents)}</td>
                  <td className="py-2 text-right font-semibold">{formatCents(x.summary.currentDueCents)}</td>
                  <td className="px-5 py-2">
                    <div className="flex flex-wrap items-center gap-3">
                      {x.invoice && (
                        <button className="font-semibold text-brand hover:underline" onClick={() => onOpenInvoice(x.invoice!.id)}>
                          Invoice {x.invoice.number}
                        </button>
                      )}
                      {x.status === "void" && <Badge tone="off">Void</Badge>}
                      {x.pdfUrl && (
                        <a href={x.pdfUrl} target="_blank" rel="noreferrer" className="text-brand hover:underline">
                          PDF
                        </a>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {pg.release && (
                <tr className="border-b border-mist">
                  <td className="px-5 py-2 font-semibold" colSpan={4}>
                    Retainage release
                  </td>
                  <td className="py-2 text-right font-semibold">{formatCents(pg.retainageHeldCents)}</td>
                  <td className="px-5 py-2">
                    <button className="font-semibold text-brand hover:underline" onClick={() => onOpenInvoice(pg.release!.id)}>
                      Invoice {pg.release.number}
                    </button>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      <p className="px-5 py-3 text-xs text-muted">
        Each pay app makes its invoice (work this period, less retainage) and a pay application PDF in the job's files. Approved change orders join the schedule of values here; don't invoice them separately. Only the latest pay app can be voided (void its invoice).
      </p>
      {billing && <PayAppDialog pg={pg} onClose={() => setBilling(false)} onBilled={async () => (await refresh(), setBilling(false))} />}
    </Panel>
  );
}

/** "45", "45%" (percent of the line), or "$12,000" (dollars to date). */
function parseToDate(text: string, line: ScheduleLine): number | null {
  const t = text.trim();
  if (!t) return null;
  if (t.startsWith("$") || t.startsWith("-$")) {
    const neg = t.startsWith("-") || line.valueCents < 0;
    const c = parseDollars(t.replace(/^-/, ""));
    return c === null || Number.isNaN(c) ? Number.NaN : neg ? -c : c;
  }
  const p = parsePercentMilli(t);
  return p === null ? Number.NaN : percentOf(line.valueCents, p);
}

function PayAppDialog({ pg, onClose, onBilled }: { pg: ProgressBilling; onClose: () => void; onBilled: () => void }) {
  const today = new Intl.DateTimeFormat("en-CA", { timeZone: "America/Chicago", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
  const [periodTo, setPeriodTo] = useState(today);
  const [work, setWork] = useState<Record<string, string>>({});
  const [stored, setStored] = useState<Record<string, string>>({});
  const entries = useMemo(() => {
    const out: PayAppEntry[] = [];
    let bad = false;
    for (const l of pg.schedule) {
      const w = work[l.key] !== undefined && work[l.key]!.trim() ? parseToDate(work[l.key]!, l) : (pg.previous.workToDate[l.key] ?? 0);
      const sRaw = stored[l.key]?.trim() ? parseDollars(stored[l.key]!) : 0;
      if (w === null || Number.isNaN(w) || sRaw === null || Number.isNaN(sRaw)) bad = true;
      out.push({ key: l.key, workToDateCents: Number.isNaN(w ?? 0) ? 0 : (w ?? 0), storedCents: Number.isNaN(sRaw ?? 0) ? 0 : (sRaw ?? 0) * (l.valueCents < 0 ? -1 : 1) });
    }
    return { out, bad };
  }, [work, stored, pg]);
  const r = useMemo(() => computePayApp(pg.schedule, pg.originalCents, entries.out, pg.previous, pg.retainage), [entries, pg]);
  const bill = useMutation({ mutationFn: () => post(`/api/proposals/${pg.proposalId}/pay-apps`, { periodTo, entries: entries.out }), onSuccess: onBilled });
  const problems = [...(entries.bad ? ["Check the numbers: like 45%, or $12,000 for dollars."] : []), ...r.problems];
  const s = r.summary;
  return (
    <Dialog
      wide="xl"
      title={`Pay application #${pg.nextNumber}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" busy={bill.isPending} disabled={problems.length > 0 || !periodTo} onClick={() => bill.mutate()}>
            Bill {formatCents(s.currentDueCents)}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {bill.error && <Alert>{errorMessage(bill.error)}</Alert>}
        <div className="flex flex-wrap items-end gap-4">
          <Field label="Period to" type="date" value={periodTo} onChange={(e) => setPeriodTo(e.target.value)} className="w-48" />
          <p className="flex-1 text-sm text-muted">For each line, enter the work completed to date: a percent (45%) or dollars ($12,000).{pg.storedMaterials ? " Materials stored on site but not installed go in Stored." : ""}</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="text-left text-xs text-muted">
              <tr className="border-b border-mist">
                <th className="py-2 font-semibold">Line</th>
                <th className="py-2 text-right font-semibold">Value</th>
                <th className="py-2 text-right font-semibold">Done before</th>
                <th className="w-32 py-2 pl-3 font-semibold">Done to date</th>
                {pg.storedMaterials && <th className="w-28 py-2 pl-3 font-semibold">Stored</th>}
                <th className="py-2 text-right font-semibold">This period</th>
                <th className="py-2 text-right font-semibold">To date</th>
              </tr>
            </thead>
            <tbody>
              {r.lines.map((l) => {
                const prevPct = l.valueCents ? Math.round((l.previousCents / l.valueCents) * 100) : 0;
                return (
                  <tr key={l.key} className="border-b border-mist">
                    <td className="py-1.5 pr-2">{l.description}</td>
                    <td className="py-1.5 text-right">{formatCents(l.valueCents)}</td>
                    <td className="py-1.5 text-right text-muted">
                      {formatCents(l.previousCents)} ({prevPct}%)
                    </td>
                    <td className="py-1.5 pl-3">
                      <input
                        aria-label={`${l.description}: done to date`}
                        className="w-full rounded-[var(--radius-control)] border border-line px-2 py-1 text-right text-base sm:text-sm"
                        placeholder={`${prevPct}%`}
                        value={work[l.key] ?? ""}
                        onChange={(e) => setWork({ ...work, [l.key]: e.target.value })}
                      />
                    </td>
                    {pg.storedMaterials && (
                      <td className="py-1.5 pl-3">
                        <input aria-label={`${l.description}: stored`} className="w-full rounded-[var(--radius-control)] border border-line px-2 py-1 text-right text-base sm:text-sm" placeholder="$0" value={stored[l.key] ?? ""} onChange={(e) => setStored({ ...stored, [l.key]: e.target.value })} />
                      </td>
                    )}
                    <td className="py-1.5 text-right">{formatCents(l.thisPeriodCents)}</td>
                    <td className="py-1.5 text-right">
                      {formatCents(l.toDateCents)} <span className="text-xs text-muted">{l.percentMilli === null ? "" : `${Math.round(l.percentMilli / 1000)}%`}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <dl className="grid gap-x-6 gap-y-1 rounded-[var(--radius-control)] bg-mist px-4 py-3 text-sm sm:grid-cols-2">
          {[
            ["Contract to date", s.contractToDateCents],
            ["Completed and stored to date", s.completedToDateCents],
            [`Retainage held (${formatPercentMilli(s.retainagePercentMilli)})`, s.retainageCents],
            ["Earned less retainage", s.earnedLessRetainageCents],
            ["Less earlier pay apps", s.previousCertificatesCents],
            ["Balance to finish (with retainage)", s.balanceToFinishCents],
          ].map(([k, v]) => (
            <div key={k as string} className="flex justify-between gap-3">
              <dt className="text-ink-soft">{k}</dt>
              <dd>{formatCents(v as number)}</dd>
            </div>
          ))}
          <div className="flex justify-between gap-3 border-t border-line pt-1 font-semibold sm:col-span-2">
            <dt>Due on this pay app</dt>
            <dd>{formatCents(s.currentDueCents)}</dd>
          </div>
        </dl>
        {problems.map((m) => (
          <div key={m} className="text-sm text-danger">
            {m}
          </div>
        ))}
      </div>
    </Dialog>
  );
}
